import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, HelpCircle, UserPlus, ChevronLeft, Search, Ticket, AlertTriangle, QrCode } from "lucide-react";
import { QrCheckin } from "@/components/QrCheckin";

import { PaidToggle } from "@/components/PaidToggle";
import { useLive } from "@/lib/useLive";
import { enqueue, isOffline, manualOpenNow, cachedGet, overlayAttendance, getQueue, flushQueue, QUEUE_EVENT } from "@/lib/attQueue";
import { CloudOff } from "lucide-react";

const ST = {
  confirmed: { t: "Asistirá", c: "bg-emerald-600 text-white", i: CheckCircle2 },
  declined: { t: "No asistirá", c: "bg-rose-600 text-white", i: XCircle },
  sin_respuesta: { t: "Sin confirmar", c: "bg-muted text-foreground", i: HelpCircle },
};
const INV = { pending: "Invitación pendiente", accepted: "Aceptó", declined: "Rechazó", cancelled: "Anulada" };

function Stat({ n, label, cls, testId }) {
  return <div className={`rounded-lg p-2 text-center ${cls}`} data-testid={testId}><p className="font-extrabold text-lg leading-none">{n}</p><p className="text-[10px] mt-1">{label}</p></div>;
}

export function InvitePicker({ s, onDone }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/business/credit-holders", { params: { session_id: s.id, q: q || undefined } }).then((r) => setRows(r.data)), 250);
    return () => clearTimeout(t);
  }, [q, s.id]);
  const invite = async (h) => {
    setBusy(h.email);
    try { await api.post(`/business/sessions/${s.id}/invite`, { customer_email: h.email }); toast.success(`Invitación enviada a ${h.name || h.email}`); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  return (
    <div className="rounded-xl border p-3 space-y-2" data-testid="invite-picker">
      <p className="text-sm font-semibold">Invitar alumno con créditos contigo</p>
      <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-8" placeholder="Buscar por nombre o email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="invite-search" /></div>
      {!rows ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : rows.length === 0 ? <p className="text-xs text-muted-foreground py-2">No hay alumnos con créditos disponibles.</p> : (
        <div className="divide-y max-h-56 overflow-y-auto">
          {rows.map((h) => (
            <div key={h.email} className="flex items-center gap-2 py-2" data-testid={`holder-${h.email}`}>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{h.name || "—"}</p><p className="text-xs text-muted-foreground truncate">{h.email} · {h.credits} crédito(s)</p></div>
              <Button size="sm" onClick={() => invite(h)} disabled={!!busy} data-testid={`invite-btn-${h.email}`}>{busy === h.email ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invitar"}</Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ATT = {
  presente: { t: "Presente", row: "border-l-emerald-500" },
  ausente: { t: "Ausente", row: "border-l-rose-500 bg-rose-50/40 dark:bg-rose-950/10" },
  pendiente: { t: "Por marcar", row: "border-l-transparent" },
};
const owes = (x) => x.direct && !x.direct_paid && x.direct_price_clp > 0;

export function AttButtons({ x, s, reload }) {
  const [busy, setBusy] = useState(null);
  const set = async (present) => {
    setBusy(present ? "p" : "a");
    const offline = () => { enqueue({ type: "manual", reservation_id: x.reservation_id, session_id: s.id, present, label: x.name }); toast.info("Sin señal: marca guardada en este teléfono. Se enviará sola al volver la conexión."); reload(); };
    if (!navigator.onLine) { offline(); setBusy(null); return; }
    try { await api.post(`/business/attendance/${x.reservation_id}/${s.id}/manual`, { present }, { offlineFallback: true }); reload(); }
    catch (e) { if (isOffline(e)) offline(); else toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const open = manualOpenNow(s);
  const btn = (on, onCls) => `h-8 px-2.5 rounded-full text-[11px] font-semibold border inline-flex items-center gap-1 transition-colors disabled:opacity-50 ${on ? onCls : "border-border hover:bg-muted"}`;
  return (
    <div className="flex gap-1 shrink-0" title={open ? "" : "Disponible desde que comienza la sesión y hasta 48 h después"}>
      <button type="button" disabled={!open || !!busy} onClick={() => set(true)} className={btn(x.attendance === "presente", "bg-emerald-600 border-emerald-600 text-white")} data-testid={`att-present-${x.reservation_id}`}>
        {busy === "p" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}Asistió</button>
      <button type="button" disabled={!open || !!busy} onClick={() => set(false)} className={btn(x.attendance === "ausente", "bg-rose-600 border-rose-600 text-white")} data-testid={`att-absent-${x.reservation_id}`}>
        {busy === "a" ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}No asistió</button>
    </div>
  );
}

function StudentRow({ x, s, reload }) {
  const st = ST[x.status];
  const a = ATT[x.attendance] || ATT.pendiente;
  return (
    <div className={`p-2.5 border-l-4 ${a.row}`} data-testid={`att-row-${x.reservation_id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{x.name}{x.canje && <Badge className="ml-1.5 bg-primary/10 text-primary hover:bg-primary/10 text-[10px] px-1.5 py-0" data-testid={`att-canje-${x.reservation_id}`}><Ticket className="w-3 h-3 mr-0.5" />Canje{x.canje_by === "alumno" ? "" : " · invitación"}</Badge>}</p>
          <p className="text-[11px] text-muted-foreground truncate">{x.quantity} cupo(s) · El alumno indicó: <span className="inline-flex items-center gap-0.5"><st.i className="w-3 h-3" />{st.t}</span></p>
        </div>
        <span className={`shrink-0 text-[11px] font-bold ${x.attendance === "presente" ? "text-emerald-700" : x.attendance === "ausente" ? "text-rose-700" : "text-muted-foreground"}`} data-testid={`att-state-${x.reservation_id}`}>
          {a.t}{x.attendance === "presente" && x.checkin_method && ` · ${x.checkin_method === "ml" ? "manual" : "QR"}`}
          {x.pending_sync && <span className="ml-1 inline-flex items-center gap-0.5 text-amber-700" data-testid={`att-pending-${x.reservation_id}`}><CloudOff className="w-3 h-3" />por enviar</span>}</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
        <AttButtons x={x} s={s} reload={reload} />
        <div className="flex items-center gap-1.5">
          {owes(x) && x.attendance === "presente" && <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-amber-700" data-testid={`att-owes-${x.reservation_id}`}><AlertTriangle className="w-3.5 h-3.5" />Debe</span>}
          <PaidToggle x={x} onChange={reload} />
        </div>
      </div>
    </div>
  );
}

const FILTERS = [
  { id: "all", t: "Todos", f: () => true },
  { id: "tomark", t: "Por marcar", f: (x) => x.attendance === "pendiente" },
  { id: "present", t: "Presentes", f: (x) => x.attendance === "presente" },
  { id: "owing", t: "Deben", f: owes },
];

function SessionDetail({ s, onBack, reload }) {
  const [picking, setPicking] = useState(false);
  const [scan, setScan] = useState(false);
  const [q, setQ] = useState("");
  const [flt, setFlt] = useState("all");
  const norm = (v) => (v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const rows = s.students.filter((x) => FILTERS.find((f) => f.id === flt).f(x) && (!q || norm(`${x.name} ${x.email}`).includes(norm(q))));
  const cancelInv = async (id) => {
    try { await api.post(`/business/invites/${id}/cancel`); toast.success("Invitación anulada"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="session-detail">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="session-detail-back"><ChevronLeft className="w-4 h-4 mr-1" />Sesiones</Button>
      <p className="font-semibold">{formatDateTime(s.starts_at)}</p>
      <PendingSyncBar />
      <div className="grid grid-cols-4 gap-1.5">
        <Stat n={s.present} label="Presentes" cls="bg-emerald-50 text-emerald-700" testId="sd-present" />
        <Stat n={s.absent} label="Ausentes" cls="bg-rose-50 text-rose-700" testId="sd-absent" />
        <Stat n={s.to_mark} label="Por marcar" cls="bg-muted" testId="sd-tomark" />
        <Stat n={s.owing} label="Deben" cls="bg-amber-50 text-amber-800" testId="sd-owing" />
      </div>
      {s.scan_open && (scan ? <div className="rounded-xl border p-3"><QrCheckin onScanned={reload} /><Button variant="ghost" size="sm" className="w-full mt-2" onClick={() => setScan(false)}>Cerrar escáner</Button></div>
        : <Button className="w-full" onClick={() => setScan(true)} data-testid="sd-open-scan"><QrCode className="w-4 h-4 mr-1.5" />Escanear QR del alumno</Button>)}
      {!manualOpenNow(s) && <p className="text-[11px] text-muted-foreground">Podrás marcar Asistió / No asistió desde que comience la sesión y hasta 48 h después. Tu marca reemplaza lo que indicó el alumno.</p>}
      <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-8" placeholder="Buscar alumno" value={q} onChange={(e) => setQ(e.target.value)} data-testid="sd-search" /></div>
      <div className="flex gap-1.5 flex-wrap">
        {FILTERS.map((f) => <button key={f.id} type="button" onClick={() => setFlt(f.id)} className={`h-7 px-3 rounded-full text-xs font-semibold border ${flt === f.id ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`} data-testid={`sd-filter-${f.id}`}>{f.t}</button>)}
      </div>
      <div className="divide-y rounded-xl border overflow-hidden">
        {rows.map((x) => <StudentRow key={x.reservation_id} x={x} s={s} reload={reload} />)}
        {rows.length === 0 && <p className="p-4 text-sm text-center text-muted-foreground">{s.students.length ? "Ningún alumno coincide." : "Sin alumnos inscritos."}</p>}
      </div>
      {s.invites.length > 0 && (
        <div className="space-y-1.5" data-testid="session-invites">
          <p className="text-sm font-semibold">Invitaciones</p>
          {s.invites.map((i) => (
            <div key={i.id} className="flex items-center gap-2 text-sm rounded-lg border p-2" data-testid={`inv-row-${i.id}`}>
              <span className="flex-1 truncate">{i.customer_name || i.customer_email}</span>
              <Badge variant="outline">{INV[i.status]}</Badge>
              {i.status === "pending" && <Button size="sm" variant="ghost" onClick={() => cancelInv(i.id)} data-testid={`inv-cancel-${i.id}`}>Anular</Button>}
            </div>
          ))}
        </div>
      )}
      {!s.past && (s.open_spots > 0
        ? (picking ? <InvitePicker s={s} onDone={() => { setPicking(false); reload(); }} />
          : <Button className="w-full" onClick={() => setPicking(true)} data-testid="open-invite-picker"><UserPlus className="w-4 h-4 mr-1.5" />Invitar a {s.open_spots} cupo(s) liberado(s)</Button>)
        : <p className="text-xs text-muted-foreground text-center">No hay cupos liberados para invitar.</p>)}
    </div>
  );
}

export function PendingSyncBar() {
  const [n, setN] = useState(() => getQueue().length);
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const upd = () => { setN(getQueue().length); setOnline(navigator.onLine); };
    [QUEUE_EVENT, "online", "offline"].forEach((ev) => window.addEventListener(ev, upd));
    return () => [QUEUE_EVENT, "online", "offline"].forEach((ev) => window.removeEventListener(ev, upd));
  }, []);
  if (!n && online) return null;
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs flex items-center gap-2" data-testid="pending-sync-bar">
      <CloudOff className="w-4 h-4 shrink-0" />
      <span className="flex-1">{online ? `${n} marca(s) por enviar.` : `Sin señal: puedes seguir pasando lista.${n ? ` ${n} marca(s) se enviarán al volver la conexión.` : ""}`}</span>
      {online && n > 0 && <button type="button" className="font-bold underline" onClick={() => flushQueue()} data-testid="pending-sync-now">Enviar ahora</button>}
    </div>
  );
}

export function SessionAttendanceDialog({ w, onClose, sessionId = null }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const wid = w?.id;
  const load = useCallback(() => wid && cachedGet(`/business/workshops/${wid}/attendance`).then((r) => setData(overlayAttendance(r.data))).catch(() => {}), [wid]);
  useEffect(() => { window.addEventListener(QUEUE_EVENT, load); return () => window.removeEventListener(QUEUE_EVENT, load); }, [load]);
  useEffect(() => { setData(null); setSel(sessionId); load(); }, [load, sessionId]);
  useLive(load, 5000, !!w);
  if (!w) return null;
  const current = sel && data?.sessions.find((s) => s.id === sel);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="attendance-dialog">
        <DialogHeader>
          <DialogTitle>Gestión de alumnos</DialogTitle>
          <DialogDescription>{w.name}{data && ` · los alumnos avisan hasta ${data.workshop.attendance_notice_hours} h antes`}</DialogDescription>
        </DialogHeader>
        {!data ? <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" /> : current ? <SessionDetail s={current} onBack={() => setSel(null)} reload={load} /> : (
          <div className="space-y-2.5">
            {data.sessions.map((s) => (
              <button key={s.id} type="button" onClick={() => setSel(s.id)} className={`w-full text-left rounded-xl border p-3 hover:border-primary transition-colors ${s.past ? "opacity-60" : ""}`} data-testid={`att-session-${s.id}`}>
                <div className="flex justify-between items-center gap-2"><p className="font-semibold text-sm">{formatDateTime(s.starts_at)}</p>
                  <div className="flex gap-1.5 shrink-0">
                    {s.canjes > 0 && <Badge className="bg-primary/10 text-primary hover:bg-primary/10" data-testid={`att-canjes-${s.id}`}><Ticket className="w-3 h-3 mr-1" />{s.canjes} canje{s.canjes === 1 ? "" : "s"}</Badge>}
                    {s.open_spots > 0 && !s.past && <Badge className="bg-sun text-[#3d2b00] hover:bg-sun">{s.open_spots} cupo(s) liberado(s)</Badge>}
                  </div></div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {s.past ? (<>
                    <Stat n={s.present} label="Presentes" cls="bg-emerald-50 text-emerald-700" testId={`att-present-n-${s.id}`} />
                    <Stat n={s.to_mark} label="Por marcar" cls="bg-muted" testId={`att-tomark-${s.id}`} />
                    <Stat n={s.owing} label="Deben" cls="bg-amber-50 text-amber-800" testId={`att-owing-${s.id}`} />
                  </>) : (<>
                  <Stat n={s.confirmed} label="Asistirán" cls="bg-emerald-50 text-emerald-700" testId={`att-confirmed-${s.id}`} />
                  <Stat n={s.no_reply} label="Sin confirmar" cls="bg-muted" testId={`att-noreply-${s.id}`} />
                  <Stat n={s.declined} label="No asistirán" cls="bg-rose-50 text-rose-700" testId={`att-declined-${s.id}`} />
                  </>)}
                </div>
              </button>
            ))}
            {data.sessions.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Este taller no tiene sesiones.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
